import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AuthErrorBoundary } from './components/AuthErrorBoundary';
import { Header } from './components/Header';
import { OfflineBanner } from './components/OfflineBanner';
import { DashboardPage } from './pages/Dashboard';
import { SettingsPage } from './pages/Settings';
import { SettingsCalendarFeedPage } from './pages/SettingsCalendarFeed';
import { SettingsDocumentKindsPage } from './pages/SettingsDocumentKinds';
import { CustomerListPage } from './features/customers/CustomerListPage';
import { CustomerNewPage } from './features/customers/CustomerNewPage';
import { CustomerDetailPage } from './features/customers/CustomerDetailPage';
import { RecordConfigPage } from './features/customers/RecordConfigPage';
import { VCardImportPage } from './features/customers/VCardImportPage';
import { ReceiptListPage } from './features/receipts/ReceiptListPage';
import { NewDepositPage } from './features/receipts/NewDepositPage';
import { ReceiptDetailPage } from './features/receipts/ReceiptDetailPage';
import { PublicReceiptViewer } from './features/receipts/PublicReceiptViewer';
import { EstimateListPage } from './features/estimates/EstimateListPage';
import { NewEstimatePage } from './features/estimates/NewEstimatePage';
import { EstimateDetailPage } from './features/estimates/EstimateDetailPage';
import { PublicEstimateViewer } from './features/estimates/PublicEstimateViewer';
import { CalendarPage } from './features/visits/CalendarPage';
import { VisitDetailPage } from './features/visits/VisitDetailPage';
import { CloseVisitWizard } from './features/receipts/CloseVisitWizard';
import { ReportsPage } from './features/reports/ReportsPage';
import { DocumentsHomePage } from './features/documents/DocumentsHomePage';
import { TemplatesPage } from './features/documents/TemplatesPage';
import { PackagesPage } from './features/documents/PackagesPage';
import { PackageEditor } from './features/documents/PackageEditor';
import { SendWizardPage } from './features/documents/SendWizardPage';
import { SentDocumentsPage } from './features/documents/SentDocumentsPage';
import { ValidityReportPage } from './features/documents/ValidityReportPage';
import { PublicSignPage } from './features/documents/PublicSignPage';
import { PublicVerifyPage } from './features/documents/PublicVerifyPage';
import { PublicDocumentViewPage } from './features/documents/PublicDocumentViewPage';
import Landing from './pages/Landing';
import { SignupEntry } from './features/billing/SignupEntry';
import { CheckoutSuccess } from './features/billing/CheckoutSuccess';
import { CheckoutCancel } from './features/billing/CheckoutCancel';
import { OnboardingPage } from './features/onboarding/OnboardingPage';
import { OnboardingProfilePage } from './features/onboarding/OnboardingProfilePage';
import { useOnboarding } from './features/onboarding/hooks';
import { MarketingPage } from './features/marketing/MarketingPage';
import { BottomNav } from './components/BottomNav';
import { useMe } from './api/hooks';
import { acquireToken } from './auth/tokenRef';
import { SessionWatcher } from './auth/SessionWatcher';
import { useAuthStore } from './stores/authStore';

function AuthSync() {
  const { instance, accounts } = useMsal();
  const setAccount = useAuthStore((s) => s.setAccount);
  const setReady = useAuthStore((s) => s.setReady);

  useEffect(() => {
    const active = instance.getActiveAccount() ?? accounts[0] ?? null;
    if (active && !instance.getActiveAccount()) {
      instance.setActiveAccount(active);
    }
    if (active) {
      setAccount({
        accountId: active.homeAccountId,
        username: active.username,
        displayName: active.name ?? null
      });
      void acquireToken(active);
    } else {
      setAccount(null);
    }
    setReady(true);
  }, [instance, accounts, setAccount, setReady]);

  return null;
}

// Decides what "/" shows: signed-in users go to the app home (/dashboard),
// signed-out visitors get the public marketing landing. Waits for AuthSync to
// finish so authenticated users never flash the marketing page.
function RootRoute() {
  const isReady = useAuthStore((s) => s.isReady);
  const accountId = useAuthStore((s) => s.accountId);
  if (!isReady) return null;
  return accountId ? <Navigate to="/dashboard" replace /> : <Landing />;
}

// Slim cross-app reminder while onboarding is unfinished.
function SetupBanner() {
  const { pathname } = useLocation();
  const accountId = useAuthStore((s) => s.accountId);
  const { data } = useOnboarding(!!accountId);
  if (!data || data.completed || pathname.startsWith('/onboarding')) return null;
  return (
    <div className="bg-amber-50 border-b border-amber-200 text-sm text-ink px-4 py-2 flex items-center justify-center gap-3">
      <span>Setup in progress.</span>
      <Link to="/onboarding" className="font-medium text-pine hover:text-pine-600">
        Continue setup
      </Link>
    </div>
  );
}

// Force a brand-new, unsubscribed tenant through steps 1-3. Once they've picked
// an interest (demo phase — Option A read access) or subscribed, they may roam
// the app; the banner guides them back. /onboarding and /billing are exempt.
function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const accountId = useAuthStore((s) => s.accountId);
  const { data, isLoading } = useOnboarding(!!accountId);
  const exempt = pathname.startsWith('/onboarding') || pathname.startsWith('/billing');
  if (!accountId || isLoading || !data) return <>{children}</>;
  const mustOnboard = !data.completed && !data.hasActiveSubscription && !data.interestPath;
  if (mustOnboard && !exempt) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

// Gate the marketing route on the server-driven flag. While /me is loading we
// render nothing (avoids a flash); once known, an off flag redirects to /dashboard.
function MarketingRoute() {
  const accountId = useAuthStore((s) => s.accountId);
  const { data: me, isLoading } = useMe(!!accountId);
  if (isLoading || !me) return null;
  return me.features?.marketingEnabled ? <MarketingPage /> : <Navigate to="/dashboard" replace />;
}

function AuthedShell() {
  return (
    <AuthErrorBoundary>
      <SessionWatcher />
      <div className="min-h-screen bg-slate-50">
        <OfflineBanner />
        <SetupBanner />
        <Header />
        <main>
          <OnboardingGate>
          <Routes>
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/onboarding/profile" element={<OnboardingProfilePage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/records" element={<RecordConfigPage />} />
            <Route path="/settings/calendar-feed" element={<SettingsCalendarFeedPage />} />
            <Route path="/settings/document-kinds" element={<SettingsDocumentKindsPage />} />
            <Route path="/customers" element={<CustomerListPage />} />
            <Route path="/customers/new" element={<CustomerNewPage />} />
            <Route path="/customers/import" element={<VCardImportPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/receipts" element={<ReceiptListPage />} />
            <Route path="/receipts/new-deposit" element={<NewDepositPage />} />
            <Route path="/receipts/:id" element={<ReceiptDetailPage />} />
            <Route path="/estimates" element={<EstimateListPage />} />
            <Route path="/estimates/new" element={<NewEstimatePage />} />
            <Route path="/estimates/:id" element={<EstimateDetailPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/visits/:id" element={<VisitDetailPage />} />
            <Route path="/visits/:id/close" element={<CloseVisitWizard />} />
            <Route path="/documents" element={<DocumentsHomePage />} />
            <Route path="/documents/templates" element={<TemplatesPage />} />
            <Route path="/documents/packages" element={<PackagesPage />} />
            <Route path="/documents/packages/:id" element={<PackageEditor />} />
            <Route path="/documents/send" element={<SendWizardPage />} />
            <Route path="/documents/sent" element={<SentDocumentsPage />} />
            <Route path="/documents/validity" element={<ValidityReportPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/marketing" element={<MarketingRoute />} />
          </Routes>
          </OnboardingGate>
        </main>
        <BottomNav />
      </div>
    </AuthErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      {/* Lifted out of AuthedShell so the auth store is populated on every
          route -- RootRoute below relies on it to decide "/" before the
          authed shell ever mounts. */}
      <AuthSync />
      <Routes>
        {/* Public, no-MSAL-dependent routes. The outer app is still wrapped
            in MsalProvider but these pages don't touch the header or use
            tokenRef, so a signed-out customer lands here cleanly. */}
        <Route path="/view/:nonce" element={<PublicReceiptViewer />} />
        <Route path="/estimate-view/:nonce" element={<PublicEstimateViewer />} />
        <Route path="/sign/:nonce" element={<PublicSignPage />} />
        <Route path="/signed/:nonce" element={<PublicDocumentViewPage />} />
        <Route path="/verify/:signatureId" element={<PublicVerifyPage />} />
        {/* Signup + Stripe Checkout return routes. Top-level (not inside
            AuthedShell) because they gate their own auth: /signup drives the
            login redirect itself, and the billing return pages only read the
            gate-exempt status endpoint. */}
        <Route path="/signup" element={<SignupEntry />} />
        <Route path="/billing/success" element={<CheckoutSuccess />} />
        <Route path="/billing/cancel" element={<CheckoutCancel />} />
        {/* Public marketing landing for signed-out visitors; signed-in users
            are redirected to /dashboard. */}
        <Route path="/" element={<RootRoute />} />
        {/* Everything else runs inside the authed shell. */}
        <Route path="/*" element={<AuthedShell />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
